import { useState, useEffect } from "react";
import { Modal } from "../primitives/Modal";
import { Input } from "../forms/Input";
import { Button } from "../primitives/Button";
import { Checkbox } from "../primitives/Checkbox";
import { AVAILABLE_PERMISSIONS, getDefaultPermissions, getRoleDescription } from "../../utils/permissions";
import { cn } from "../utils/cn";
import type { CustomRole, CustomRoleCreate, CustomRoleUpdate } from "../../api/admin";

type CustomRoleModalProps = {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  role?: CustomRole | null;
  baseRole?: string | null;
  onSave: (role: CustomRoleCreate | CustomRoleUpdate) => Promise<void>;
};

export function CustomRoleModal({
  open,
  onClose,
  organizationId,
  role,
  baseRole,
  onSave,
}: CustomRoleModalProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const isEditing = !!role;

  useEffect(() => {
    if (role) {
      setName(role.name);
      setDescription(role.description || "");
      // Extract permissions from the permissions dict
      // The permissions dict has structure like { "read": true, "manage_users": true, ... }
      const perms = Object.keys(role.permissions).filter((key) => role.permissions[key] === true);
      setSelectedPermissions(perms);
    } else if (baseRole) {
      // Initialize with base role's default permissions
      setName("");
      setDescription("");
      setSelectedPermissions(getDefaultPermissions(baseRole));
    } else {
      setName("");
      setDescription("");
      setSelectedPermissions([]);
    }
  }, [role, baseRole]);

  const handleTogglePermission = (permissionId: string, checked: boolean) => {
    if (checked) {
      setSelectedPermissions([...selectedPermissions, permissionId]);
    } else {
      setSelectedPermissions(selectedPermissions.filter((p) => p !== permissionId));
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      alert("Le nom du rôle est requis");
      return;
    }

    setSaving(true);
    try {
      // Build permissions dict: { "permission_id": true/false }
      const permissions: Record<string, boolean> = {};
      AVAILABLE_PERMISSIONS.forEach((perm) => {
        permissions[perm.id] = selectedPermissions.includes(perm.id);
      });

      if (isEditing) {
        await onSave({
          name,
          description: description || null,
          permissions,
        } as CustomRoleUpdate);
      } else {
        await onSave({
          name,
          description: description || null,
          base_role: baseRole || null,
          permissions,
        } as CustomRoleCreate);
      }
      onClose();
    } catch (error) {
      console.error("Failed to save custom role:", error);
      alert("Erreur lors de la sauvegarde. Veuillez réessayer.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={isEditing ? "Modifier le rôle personnalisé" : "Créer un rôle personnalisé"}
      open={open}
      onClose={onClose}
      size="lg"
      showDefaultButtons={false}
    >
      <div className="space-y-6">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text mb-1">
              Nom du rôle *
            </label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Responsable opérations"
              disabled={saving}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text mb-1">
              Description (optionnel)
            </label>
            <textarea
              className="min-h-[96px] w-full rounded-md border border-border bg-surface px-3 py-2 text-text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Décrivez le rôle et ses responsabilités..."
              rows={3}
              disabled={saving}
            />
          </div>

          {baseRole && !isEditing && (
            <div className="rounded-md border border-border bg-surface-muted p-3">
              <div className="text-xs text-muted">
                Basé sur le rôle : <strong className="text-text">{getRoleDescription(baseRole).label}</strong>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-text mb-3">
              Permissions
            </label>
            <div className="space-y-2 max-h-96 overflow-y-auto border border-border rounded-md p-4 bg-surface-muted">
              {AVAILABLE_PERMISSIONS.map((perm) => {
                const isChecked = selectedPermissions.includes(perm.id);
                return (
                  <div
                    key={perm.id}
                    className={cn(
                      "flex items-start gap-3 p-3 rounded-md border transition",
                      isChecked
                        ? "border-primary/30 bg-primary/5"
                        : "border-border bg-surface"
                    )}
                  >
                    <Checkbox
                      checked={isChecked}
                      onChange={(checked) => handleTogglePermission(perm.id, checked)}
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-text">{perm.label}</div>
                      <div className="text-xs text-muted mt-0.5">{perm.description}</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-3 text-xs text-muted">
              <strong className="text-text">Permissions sélectionnées :</strong> {selectedPermissions.length} / {AVAILABLE_PERMISSIONS.length}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-4 border-t border-border">
          <Button
            label="Annuler"
            intent="default"
            size="sm"
            onClick={onClose}
            disabled={saving}
          />
          <Button
            label={isEditing ? "Enregistrer" : "Créer"}
            intent="primary"
            size="sm"
            onClick={handleSave}
            disabled={saving || !name.trim()}
          />
        </div>
      </div>
    </Modal>
  );
}
